import BodyText from 'LuxKit/Typography/BodyText'
import useExperiencePlace from 'hooks/Experiences/useExperiencePlace'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { memo } from 'react'
import { useIntl } from 'react-intl'
import ExperienceListTileCarousel from './ExperienceListTileCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

interface Props {
  experience: App.ExperienceOffer
  pageSize?: number
  gutterSize?: number
}

function ExperienceYAMLList(props: Props) {
  const { experience, pageSize, gutterSize } = props
  const intl = useIntl()

  const place = useExperiencePlace(experience)
  const categoryId = experience.primaryCategory?.id
  const parentCategoryId = experience.primaryCategory?.parentId
  const [specificCategoryList, specificCategoryExperiences] = useExperienceSearch({
    placeId: place?.id,
    randomize: true,
    excludeIds: [experience.id],
    limit: 8,
    streamingEnabled: true,
  }, {
    categoryCodes: categoryId ? [categoryId] : undefined,
  })
  const shouldUseParentCategoryFallback =
    !!parentCategoryId &&
    parentCategoryId !== categoryId &&
    !specificCategoryList.fetching &&
    !specificCategoryList.error &&
    specificCategoryList.ids.length < 5
  const [parentCategoryList, parentCategoryExperiences] = useExperienceSearch({
    placeId: place?.id,
    randomize: true,
    excludeIds: [experience.id],
    enabled: shouldUseParentCategoryFallback,
    limit: 8,
    streamingEnabled: true,
  }, {
    categoryCodes: parentCategoryId ? [parentCategoryId] : undefined,
  })
  const useParentCategoryResults =
    shouldUseParentCategoryFallback &&
    !parentCategoryList.fetching &&
    !parentCategoryList.error &&
    parentCategoryList.ids.length > specificCategoryList.ids.length
  const parentCategoryFallbackFetching = shouldUseParentCategoryFallback && !useParentCategoryResults
  const list = useParentCategoryResults ? parentCategoryList : specificCategoryList
  const experiences = useParentCategoryResults ? parentCategoryExperiences : specificCategoryExperiences

  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', 'YAML Carousel'),
    { key: experience.id },
  )

  const fetching = (list.fetching || parentCategoryFallbackFetching) && !list.error

  return <div>
    {list.error && <BodyText variant="large">{intl.formatMessage({
      id: '8orlxzGmuksaouSlpwFP',
      defaultMessage: 'Something went wrong!',
      description: 'Error message when experience YAML list fails to load',
    })}
    </BodyText>}
    {!fetching && !list.error && list.ids.length === 0 && <BodyText variant="large">
      {intl.formatMessage({
        id: '3XP97ylZxpgzKxzthDjZ',
        defaultMessage: 'No experiences available',
        description: 'Empty state message when no experiences found in YAML list',
      })}
    </BodyText>}
    <OfferListEventsProvider tracking={tracking}>
      <ExperienceListTileCarousel
        experiences={experiences}
        listFetching={fetching}
        pageSize={pageSize}
        gutterSize={gutterSize}
        listKey={list.listKey}
      />
    </OfferListEventsProvider>
  </div>
}

export default memo(ExperienceYAMLList)
